import * as React from 'react';
import { TextareaHTMLAttributes, useCallback, useEffect } from 'react';

import { cn } from './utils/cn';
import { useForwardedRef } from './utils/use-forwarded-ref';

export type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  error?: boolean;
  autosize?: boolean;
};

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, error, autosize, ...props }, ref) => {
    const textareaRef = useForwardedRef(ref);

    const resize = () => {
      if (autosize) {
        const textarea = textareaRef.current;

        if (!textarea) return;

        textarea.style.minHeight = '0px';
        const scrollHeight = textarea.scrollHeight + 2; // 2 px extra for the border
        textarea.style.minHeight = `${scrollHeight}px`;
      }
    };

    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (autosize) {
          resize();
        }

        props.onChange?.(event);
      },
      [autosize, props.onChange, textareaRef]
    );

    useEffect(() => {
      if (autosize) {
        resize();
      }
    }, [autosize, props.value]);

    return (
      <textarea
        className={cn(
          'color-ink bits-text-body-1 flex min-h-[44px] w-full rounded-md border border-fog bg-transparent px-3 py-2 transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-smoke focus-visible:border-ink focus-visible:outline-none disabled:cursor-not-allowed disabled:bg-fog disabled:text-smoke',
          error && 'border-red-500',
          className
        )}
        ref={textareaRef}
        {...props}
        onChange={handleChange}
      />
    );
  }
);
Textarea.displayName = 'Textarea';

export { Textarea };
